import { FC, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { Box, useTheme } from "@mui/material";
import {
  ContentSectionLayout,
  FormikInputField,
  FormikSelectField,
  TwoColsLayout
} from "@vilocnv/allsetra-core";

// Data
import {
  yesNoOptionsForDropdown,
  paymentTermInDaysOptions
} from "app/data/constants";
import { useAppSelector, useDispatchOnMount, useVatValidation } from "hooks";
import { selectDashboardPaymentMethodsState } from "app/data/selectors";
import { getAllPaymentMethodsThunk } from "app/features";
import { useTranslation } from "react-i18next";
import AddressFormSection from "./VisitingAddressForm";
import VatNumberField from "components/ui/inputs/VatNumberField/VatNumberField";
import {
  findCountryAlpha2,
  isBusinessCustomer,
  isEuVatCountry
} from "app/data/helpers/vatHelpers";

interface Props {
  countries: Array<any>;
  countriesLoading: boolean;
}

const FinancialInformationForm: FC<Props> = ({
  countries,
  countriesLoading
}) => {
  const theme = useTheme();
  const { values, initialValues, status, setStatus, validateForm } =
    useFormikContext<any>();

  const { paymentMethods, loading: paymentMethodsLoading } = useAppSelector(
    selectDashboardPaymentMethodsState
  );

  const { t } = useTranslation(["translation", "formFieldsTranslation"]);

  const safePaymentMethods = Array.isArray(paymentMethods)
    ? paymentMethods
    : [];

  useDispatchOnMount(
    getAllPaymentMethodsThunk,
    safePaymentMethods.length ? undefined : true
  );

  const vatCountryCode = findCountryAlpha2(
    countries ?? [],
    values.billingAddress?.countryId
  );
  const vatRequired =
    isBusinessCustomer(initialValues) && isEuVatCountry(vatCountryCode);

  const vat = useVatValidation({
    value: values.vatNumber,
    countryCode: vatCountryCode,
    initialValue: initialValues.vatNumber,
    initialCountryCode: findCountryAlpha2(
      countries ?? [],
      initialValues.billingAddress?.countryId
    ),
    status,
    setStatus
  });

  // The VAT rules need the billing country's code, so validate again when the
  // countries arrive after the form. Never on mount: Formik already validates
  // then, and a second run could land after it with older values.
  const countriesLoaded = (countries?.length ?? 0) > 0;
  const countriesWereLoaded = useRef(countriesLoaded);
  useEffect(() => {
    if (countriesLoaded && !countriesWereLoaded.current) validateForm();
    countriesWereLoaded.current = countriesLoaded;
  }, [countriesLoaded, validateForm]);

  return (
    <ContentSectionLayout
      title={t("titles.financialInfo")}
      subTitle={t("descriptions.financialInfo")}
    >
      <Box
        sx={{
          marginTop: {
            xs: "32px",
            md: "0px"
          }
        }}
      >
        <TwoColsLayout>
          <FormikInputField
            theme={theme}
            label={t("afasDebitNumber.label", { ns: "formFieldsTranslation" })}
            placeholder={t("afasDebitNumber.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="afasDebitNumber"
            fullWidth
          />
        </TwoColsLayout>

        <AddressFormSection
          title={t("titles.billingAddress")}
          parentKey="billingAddress"
          countries={countries ?? []}
          countriesLoading={countriesLoading}
          translator={t}
        />
        <TwoColsLayout>
          <FormikSelectField
            theme={theme}
            label={t("paymentMethod.label", { ns: "formFieldsTranslation" })}
            placeholder={t("paymentMethod.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="paymentMethod"
            options={safePaymentMethods}
            optionLabelKey={"name"}
            optionValueKey={"name"}
            loading={paymentMethodsLoading}
            fullWidth
          />
          <FormikSelectField
            theme={theme}
            label={t("paymentTerm.label", { ns: "formFieldsTranslation" })}
            placeholder={t("paymentTerm.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="paymentTermInDays"
            options={paymentTermInDaysOptions(t)}
            optionLabelKey={"name"}
            optionValueKey={"days"}
            fullWidth
          />
        </TwoColsLayout>
        <TwoColsLayout>
          <VatNumberField
            label={t("vatNumber.label", { ns: "formFieldsTranslation" })}
            placeholder={t("vatNumber.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="vatNumber"
            required={vatRequired}
            status={vat.status}
            onBlur={vat.flush}
          />
          <FormikSelectField
            theme={theme}
            label={t("vatShifted.label", { ns: "formFieldsTranslation" })}
            placeholder={t("vatShifted.placeholder", {
              ns: "formFieldsTranslation"
            })}
            options={yesNoOptionsForDropdown(t)}
            optionLabelKey={"label"}
            optionValueKey={"value"}
            name="vatShifted"
            fullWidth
          />
        </TwoColsLayout>
        <TwoColsLayout hideDivider>
          <FormikInputField
            theme={theme}
            label={t("bankAccount.label", { ns: "formFieldsTranslation" })}
            placeholder={t("bankAccount.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="bankAccount"
            fullWidth
            required
          />
        </TwoColsLayout>
      </Box>
    </ContentSectionLayout>
  );
};
export default FinancialInformationForm;
